import { toast } from 'sonner';
import { browserApiOrigin } from '@projoint/shared';

import { getAccessToken, useAuthStore } from '../auth/auth-store';

const API_BASE = browserApiOrigin(import.meta.env.VITE_API_URL, window.location.origin);

/** Default access-token lifetime (matches JWT_ACCESS_TTL=15m server-side). */
const ACCESS_TTL_MS = 15 * 60 * 1000;

/** Throttle duplicate session-expired toasts when many parallel queries hit 401. */
let lastSessionExpiredToastAt = 0;
function notifySessionExpired(): void {
  const now = Date.now();
  if (now - lastSessionExpiredToastAt < 5_000) return;
  lastSessionExpiredToastAt = now;
  toast.error('Session expired', { description: 'Please sign in again.' });
}

export interface ApiErrorBody {
  error: {
    code: string;
    message: string;
    details?: unknown;
    correlationId?: string;
    [key: string]: unknown;
  };
}

export class ApiError extends Error {
  constructor(public readonly status: number, public readonly body: ApiErrorBody) {
    super(body.error.message);
    this.name = 'ApiError';
  }

  get code(): string {
    return this.body.error.code;
  }
}

interface ApiOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  idempotencyKey?: string;
  unauthenticated?: boolean;
  skipRefresh?: boolean;
  headers?: Record<string, string>;
}

/**
 * Admin fetch wrapper.  Same contract as apps/web client.ts but uses the
 * admin auth store (separate localStorage namespace, shorter session
 * timeout).  Refresh-on-401 retries once; failure clears the store and the
 * router-level guard in __root.tsx redirects to /login.
 */
export async function api<T = unknown>(path: string, opts: ApiOptions = {}): Promise<T> {
  const url = path.startsWith('http') ? path : `${API_BASE}${path}`;
  const init = buildRequestInit(opts);

  let response = await fetch(url, init);

  if (response.status === 401 && !opts.unauthenticated && !opts.skipRefresh) {
    const refreshed = await silentRefresh();
    if (refreshed) {
      response = await fetch(url, buildRequestInit(opts));
    } else {
      useAuthStore.getState().clear();
      notifySessionExpired();
    }
  }

  return parseResponse<T>(response);
}

function buildRequestInit(opts: ApiOptions): RequestInit {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    // Declare the app so the API names the refresh cookie `projoint_admin_rt`
    // (web uses `projoint_rt`) — the two SPAs share one API origin and would
    // otherwise clobber each other's session cookie.
    'X-Projoint-App': 'admin',
    ...(opts.headers ?? {}),
  };
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  if (opts.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey;
  if (!opts.unauthenticated) {
    const tok = getAccessToken();
    if (tok) headers.Authorization = `Bearer ${tok}`;
  }
  const init: RequestInit = {
    method: opts.method ?? 'GET',
    headers,
    credentials: 'include',
  };
  if (opts.body !== undefined) init.body = JSON.stringify(opts.body);
  return init;
}

async function parseResponse<T>(response: Response): Promise<T> {
  if (response.status === 204) return undefined as T;
  const text = await response.text();
  const json = text ? (JSON.parse(text) as unknown) : undefined;
  if (response.ok) return json as T;
  // Errors always carry { error: { code, message, ... } }.  If not — synthesise.
  const body: ApiErrorBody =
    json && typeof json === 'object' && 'error' in json
      ? (json as ApiErrorBody)
      : { error: { code: `http_${response.status}`, message: response.statusText || 'Request failed' } };
  throw new ApiError(response.status, body);
}

/**
 * Mint a fresh access token from the httpOnly `projoint_admin_rt` cookie.
 *
 * The refresh token travels in the cookie (`credentials:'include'` sends it),
 * never in the body — so JS doesn't touch it.  Used for the 401-retry above and
 * for the boot silent-refresh in the root route's `beforeLoad`.  Returns true
 * iff a new access token was set.
 */
export async function silentRefresh(): Promise<boolean> {
  try {
    const init = buildRequestInit({
      method: 'POST',
      body: {},
      unauthenticated: true,
      skipRefresh: true,
    });
    const r = await fetch(`${API_BASE}/api/auth/refresh`, init);
    if (!r.ok) return false;
    const payload = (await r.json()) as { accessToken: string };
    useAuthStore.getState().setTokens({
      accessToken: payload.accessToken,
      accessExpiresAt: Date.now() + ACCESS_TTL_MS,
    });
    return true;
  } catch {
    return false;
  }
}
